import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import MediaManager from '../../components/admin/MediaManager';
import { Plus, Trash2, Edit, Loader2, X, User, ChevronDown, Pencil, Image as ImageIcon } from 'lucide-react'; // AlertTriangle removed
import { useNotification } from '../../context/NotificationContext';
import DeleteConfirmationModal from '../../components/admin/DeleteConfirmationModal'; // New import

const TeamManager = () => {
  const { addNotification } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  
  // Delete Confirmation State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title_id: '',
    short_description_id: '',
    project_type_id: '',
    status: 'published',
    file: null,
    existing_image: ''
  });

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/portfolio.php`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const handleMediaSelect = (url) => {
    setFormData(prev => ({
      ...prev,
      existing_image: url,
      file: null
    }));
    setShowMediaModal(false);
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    if (formData.id) data.append('id', formData.id);
    data.append('title_id', formData.title_id);
    data.append('project_type_id', formData.project_type_id);
    data.append('short_description_id', formData.short_description_id);
    data.append('status', formData.status);
    if (formData.file) {
      data.append('image', formData.file);
    } else if (formData.existing_image) {
      data.append('existing_image', formData.existing_image);
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/portfolio.php`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowModal(false);
      setFormData({ title_id: '', short_description_id: '', project_type_id: '', status: 'published', file: null, existing_image: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      addNotification('Data tim berhasil disimpan!', 'success');
      fetchPortfolio();
    } catch (err) {
      addNotification('Gagal menyimpan data tim', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const data = new FormData();
      data.append('id', id);
      data.append('status', newStatus);
      data.append('only_status', 'true');

      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/portfolio.php`, data);
      
      // Optimistic update
      setItems(items.map(item => 
        item.id === id ? { ...item, status: newStatus } : item
      ));
      addNotification('Status berhasil diperbarui', 'success');
    } catch (err) {
      addNotification('Gagal memperbarui status', 'error');
    }
  };

  const handleEdit = (item) => {
    let existingImg = '';
    const apiBase = process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api';
    const webBase = apiBase.replace('/api', '');

    if (item.cover_image) {
      existingImg = item.cover_image.startsWith('http') 
        ? item.cover_image 
        : `${webBase}/uploads/portfolio/${item.cover_image}`;
    }

    setFormData({
      id: item.id,
      title_id: item.title_id || '',
      short_description_id: item.short_description_id || '',
      project_type_id: item.project_type_id || '',
      status: item.status || 'published',
      file: null,
      existing_image: existingImg
    });
    setShowModal(true);
  };

  // Trigger Delete Modal
  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  // Actual Delete Action
  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/portfolio.php?id=${deleteTargetId}`);
      setItems(items.filter(i => i.id !== deleteTargetId));
      addNotification('Anggota tim dihapus', 'success');
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    } catch (err) {
      addNotification('Gagal menghapus', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const styles = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
    title: { fontSize: '1.5rem', fontWeight: '700' },
    addBtn: { backgroundColor: '#111', color: '#fff', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' },
    table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', fontSize: '0.875rem', color: '#6b7280' },
    td: { padding: '1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.9rem', verticalAlign: 'middle' },
    avatar: { width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', marginRight: '1rem', backgroundColor: '#f3f4f6' },
    badge: (status) => ({
      padding: '0.25rem 0.75rem', borderRadius: '99px', fontSize: '0.75rem', fontWeight: '600',
      backgroundColor: status === 'published' ? '#d1fae5' : '#f3f4f6',
      color: status === 'published' ? '#065f46' : '#374151'
    }),
    actionBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' },
    
    // Modal
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
    modal: { backgroundColor: '#fff', padding: '2rem', borderRadius: '1rem', width: '90%', maxWidth: '500px' },
    inputGroup: { marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    input: { padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem' },
    textarea: { padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', minHeight: '80px', fontFamily: 'inherit' },
    label: { fontSize: '0.875rem', fontWeight: '600' },
  };

  return (
    <DashboardLayout>
      <MediaManager 
        isOpen={showMediaModal} 
        onClose={() => setShowMediaModal(false)} 
        onSelect={handleMediaSelect}
      />

      <div style={styles.header}>
        <h1 style={styles.title}>Manajer Tim</h1>
        <button style={styles.addBtn} onClick={() => {
          setFormData({ title_id: '', short_description_id: '', project_type_id: '', status: 'published', file: null, existing_image: '' });
          setShowModal(true);
        }}>
          <Plus size={18} /> Tambah Anggota
        </button>
      </div>

      {loading ? <div>Memuat data...</div> : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Anggota</th>
              <th style={styles.th}>Jabatan</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const apiBase = process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api';
              const webBase = apiBase.replace('/api', '');
              const imageUrl = item.cover_image && !item.cover_image.startsWith('http') 
                  ? `${webBase}/uploads/portfolio/${item.cover_image}` 
                  : item.cover_image;

              return (
              <tr key={item.id}>
                <td style={styles.td}>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    {item.cover_image ? (
                      <img 
                        src={imageUrl} 
                        alt={item.title_id} 
                        style={styles.avatar} 
                      />
                    ) : (
                      <div style={{...styles.avatar, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <User size={20} color="#9ca3af" />
                      </div>
                    )}
                    <div>
                      <div style={{fontWeight: '600'}}>{item.title_id}</div>
                      <div style={{color: '#6b7280', fontSize: '0.8rem'}}>{item.short_description_id}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}>{item.project_type_id}</td>
                <td style={styles.td}>
                  <div style={{position: 'relative', width: '120px'}}>
                    <select 
                      value={item.status} 
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '99px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: item.status === 'published' ? '#d1fae5' : '#f3f4f6',
                        color: item.status === 'published' ? '#065f46' : '#374151',
                        border: 'none',
                        appearance: 'none',
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                    >
                      <option value="published">Aktif</option>
                      <option value="draft">Nonaktif</option>
                    </select>
                    <ChevronDown size={12} style={{position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.5}} />
                  </div>
                </td>
                <td style={styles.td}>
                  <div style={{display:'flex', gap:'0.5rem'}}>
                    <button style={{...styles.actionBtn, color: '#2563eb'}} onClick={() => handleEdit(item)}>
                      <Pencil size={18} />
                    </button>
                    <button style={{...styles.actionBtn, color: '#ef4444'}} onClick={() => handleDelete(item.id)}><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'1.5rem'}}>
              <h3>{formData.id ? 'Edit Anggota Tim' : 'Tambah Anggota Tim'}</h3>
              <button onClick={() => {
                setShowModal(false);
                setFormData({ title_id: '', short_description_id: '', project_type_id: '', status: 'published', file: null });
              }} style={{border:'none', background:'none', cursor:'pointer'}}><X /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nama Lengkap</label>
                <input 
                  style={styles.input} 
                  value={formData.title_id} 
                  onChange={e => setFormData({...formData, title_id: e.target.value})} 
                  required 
                  placeholder="Contoh: Kang Iman"
                />
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>Jabatan / Role</label>
                <input 
                  style={styles.input} 
                  value={formData.project_type_id} 
                  onChange={e => setFormData({...formData, project_type_id: e.target.value})} 
                  required 
                  placeholder="Contoh: Owner / Manager Produksi"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Pengalaman / Deskripsi Singkat</label>
                <textarea 
                  style={styles.textarea} 
                  value={formData.short_description_id} 
                  onChange={e => setFormData({...formData, short_description_id: e.target.value})} 
                  placeholder="Contoh: 11 tahun pengalaman di bidang konveksi..."
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Foto Profil</label>
                
                <div style={{border:'1px solid #e5e7eb', borderRadius:'0.5rem', padding:'0.5rem'}}>
                  <div 
                    onClick={() => setShowMediaModal(true)}
                    style={{
                      padding: '1rem', border: '2px dashed #d1d5db', borderRadius: '0.5rem', 
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                      cursor: 'pointer', backgroundColor: '#f9fafb', color: '#6b7280'
                    }}
                  >
                    <ImageIcon size={24} />
                    <span style={{marginTop:'0.5rem', fontSize:'0.85rem', fontWeight:'500'}}>
                      {formData.existing_image ? 'Ganti Foto' : 'Pilih Foto'}
                    </span>
                  </div>
                  
                  {formData.existing_image && (
                    <div style={{marginTop:'0.5rem', textAlign:'center'}}>
                      <img 
                        src={formData.existing_image} 
                        alt="Preview" 
                        style={{width:'100px', height:'100px', objectFit:'cover', borderRadius:'50%', border:'1px solid #eee'}} 
                      />
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Status</label>
                <select style={styles.input} value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                  <option value="published">Aktif</option>
                  <option value="draft">Nonaktif</option>
                </select>
              </div>

              <div style={{display:'flex', justifyContent:'flex-end', gap:'1rem', marginTop:'1rem'}}>
                <button type="button" onClick={() => setShowModal(false)} style={{...styles.addBtn, backgroundColor:'#e5e7eb', color:'#000'}}>Batal</button>
                <button type="submit" style={{...styles.addBtn, opacity: submitting ? 0.7 : 1}} disabled={submitting}>
                  {submitting ? <Loader2 className="animate-spin" /> : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isConfirming={deleting}
        targetName={items.find(item => item.id === deleteTargetId)?.title_id || 'anggota tim ini'}
      />
    </DashboardLayout>
  );
};

export default TeamManager;
